import React, { useState } from "react";
import { Input, Select, SelectItem } from "@heroui/react";
import { Plus, Trash } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { ContactSearchSelect } from "screens/buildings/components/renovationTab/modals/components/ContactSearchSelect";
import { CompanyShareholder } from "shared/types/yally-company";
import { Contact } from "shared/types/contact";
import CompanySearchInput from "./CompanySearchInput";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ShareholderType = "persoon" | "bedrijf";

interface CompanyShareholderListProps {
  shareholders: CompanyShareholder[];
  onChange: (shareholders: CompanyShareholder[]) => void;
  isDisabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMPTY_SHAREHOLDER: CompanyShareholder = {
  numberOfShares: undefined,
};

function resolveType(shareholder: CompanyShareholder): ShareholderType {
  if (shareholder.contactCompany) return "bedrijf";
  return "persoon";
}

// ---------------------------------------------------------------------------
// ShareholderRow — a single row in the list
// ---------------------------------------------------------------------------

interface ShareholderRowProps {
  shareholder: CompanyShareholder;
  index: number;
  isDisabled: boolean;
  onFieldChange: (
    index: number,
    patch: Partial<CompanyShareholder>
  ) => void;
  onRemove: (index: number) => void;
}

const ShareholderRow: React.FC<ShareholderRowProps> = ({
  shareholder,
  index,
  isDisabled,
  onFieldChange,
  onRemove,
}) => {
  const [type, setType] = useState<ShareholderType>(resolveType(shareholder));

  const handleTypeChange = (newType: ShareholderType) => {
    setType(newType);
    onFieldChange(index, { contact: undefined, contactCompany: undefined });
  };

  const handleContactChange = (contact: Contact | null) => {
    if (!contact) {
      onFieldChange(index, { contact: undefined });
      return;
    }
    onFieldChange(index, {
      contact: {
        id: contact.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
      },
      contactCompany: undefined,
    });
  };

  const handleContactCompanyChange = (
    company: { id: number; name: string } | null
  ) => {
    if (!company) {
      onFieldChange(index, { contactCompany: undefined });
      return;
    }
    onFieldChange(index, {
      contactCompany: { id: company.id, name: company.name },
      contact: undefined,
    });
  };

  const handleSharesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const parsed = raw === "" ? undefined : parseInt(raw, 10);
    onFieldChange(index, { numberOfShares: isNaN(parsed as number) ? undefined : parsed });
  };

  // Build a Contact-shaped value for ContactSearchSelect from the stored snapshot
  const contactValue: Contact | null = shareholder.contact
    ? ({
        id: shareholder.contact.id,
        firstName: shareholder.contact.firstName,
        lastName: shareholder.contact.lastName,
      } as Contact)
    : null;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-200 p-3">
      <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
        {/* Type selector */}
        <Select
          label="Type"
          selectedKeys={new Set([type])}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0] as ShareholderType;
            if (selected) handleTypeChange(selected);
          }}
          size="sm"
          variant="bordered"
          isDisabled={isDisabled}
          classNames={{ label: "text-sm font-medium", value: "text-sm" }}
        >
          <SelectItem key="persoon">Persoon</SelectItem>
          <SelectItem key="bedrijf">Bedrijf</SelectItem>
        </Select>

        {/* Contact or company picker */}
        <div className="flex flex-col justify-end">
          {type === "persoon" ? (
            <ContactSearchSelect
              value={contactValue}
              onChange={handleContactChange}
            />
          ) : (
            <CompanySearchInput
              value={shareholder.contactCompany ?? null}
              onChange={handleContactCompanyChange}
              isDisabled={isDisabled}
            />
          )}
        </div>

        {/* Number of shares */}
        <Input
          label="Aantal aandelen"
          type="number"
          value={
            shareholder.numberOfShares !== undefined
              ? String(shareholder.numberOfShares)
              : ""
          }
          onChange={handleSharesChange}
          placeholder="0"
          size="sm"
          variant="bordered"
          isDisabled={isDisabled}
          min={0}
          classNames={{ label: "text-sm font-medium", input: "text-sm" }}
        />
      </div>

      {!isDisabled && (
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="mt-2 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
          aria-label="Aandeelhouder verwijderen"
        >
          <Trash size={16} />
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CompanyShareholderList — main export
// ---------------------------------------------------------------------------

const CompanyShareholderList: React.FC<CompanyShareholderListProps> = ({
  shareholders,
  onChange,
  isDisabled = false,
}) => {
  const handleAdd = () => {
    onChange([...shareholders, { ...EMPTY_SHAREHOLDER }]);
  };

  const handleRemove = (index: number) => {
    onChange(shareholders.filter((_, i) => i !== index));
  };

  const handleFieldChange = (
    index: number,
    patch: Partial<CompanyShareholder>
  ) => {
    const updated = shareholders.map((item, i) =>
      i === index ? { ...item, ...patch } : item
    );
    onChange(updated);
  };

  return (
    <div className="flex flex-col gap-3">
      {shareholders.length === 0 && (
        <p className="text-sm text-gray-400">Geen aandeelhouders toegevoegd</p>
      )}

      {shareholders.map((shareholder, index) => (
        <ShareholderRow
          key={shareholder.id ?? `new-${index}`}
          shareholder={shareholder}
          index={index}
          isDisabled={isDisabled}
          onFieldChange={handleFieldChange}
          onRemove={handleRemove}
        />
      ))}

      {!isDisabled && (
        <ButtonNew
          variant="secondaryMoss"
          onClick={handleAdd}
          icon={<Plus />}
          label="Aandeelhouder toevoegen"
        />
      )}
    </div>
  );
};

export default CompanyShareholderList;
